import {
  ResourceName,
  Task,
  Claimant,
  User,
  SendEligibilitySignatureRequest,
  ClientContact,
  UserStatus,
} from "@/types";
import { Stack, Typography, Chip, Box, Button, TextField, Autocomplete, FormHelperText, Skeleton } from "@mui/material";
import { useGetList, useGetIdentity, useGetOne, Link } from "react-admin";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SendIcon from "@mui/icons-material/Send";
import { getProps } from "@/utils/helpers/columnsHelper";
import { getRelevantPolicy } from "@/utils/helpers/getRelevantPolicy";
import { formatDate } from "@/utils/formatters/dateFormatter";
import { useState } from "react";
import { emailRegex, invalidEmailErrorMessage } from "@/utils/helpers/validationHelpers";

interface ClaimantVerificationFormProps {
  task: Task;
  template: { title: string };
  claimantData?: Claimant;
  isLoading: boolean;
  step: 1 | 2;
  formData: SendEligibilitySignatureRequest;
  onFormChange: (updates: Partial<SendEligibilitySignatureRequest>) => void;
  onProceedToStep2: () => void;
  onSend: () => void;
  onCancel: () => void;
  isSending?: boolean;
}

interface DataRowProps {
  label: string;
  value?: string | null;
  isLoading?: boolean;
}

const DataRow = ({ label, value, isLoading }: DataRowProps) => (
  <Box flex={1}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    {isLoading ? (
      <Skeleton width="60%" height={20} />
    ) : (
      <Typography variant="body2" fontWeight={500}>
        {value || "—"}
      </Typography>
    )}
  </Box>
);

export const ClaimantVerificationForm = ({
  task,
  template,
  claimantData,
  isLoading,
  step,
  formData,
  onFormChange,
  onProceedToStep2,
  onSend,
  onCancel,
  isSending = false,
}: ClaimantVerificationFormProps) => {
  const [recipientTouched, setRecipientTouched] = useState(false);

  const { data: currentUser } = useGetIdentity();
  const { data: users = [], isLoading: isLoadingUsers } = useGetList<User>(ResourceName.USERS, {
    filter: { status: UserStatus.ACTIVE },
  });

  const { data: client } = useGetOne(
    ResourceName.CLIENTS,
    { id: claimantData?.client_id_ref },
    { enabled: !!claimantData?.client_id_ref }
  );

  const policy = getRelevantPolicy(claimantData?.policies || []);

  const clientContacts = (client?.contacts || [])
    .filter((c: ClientContact) => c.is_verified && c.email)
    .sort((a: ClientContact, b: ClientContact) => {
      if (a.is_primary && !b.is_primary) return -1;
      if (!a.is_primary && b.is_primary) return 1;
      return 0;
    });

  const sortedUsers = [...users].sort((a, b) => {
    const nameA = a.full_name || a.email || a.id;
    const nameB = b.full_name || b.email || b.id;
    return nameA.localeCompare(nameB);
  });

  const showReplyToError = !!formData.reply_to.email && !emailRegex.test(formData.reply_to.email);

  const subscriberDisplayName =
    claimantData?.claimant_related_subscriber_first_name && claimantData?.claimant_related_subscriber_last_name
      ? `${claimantData.claimant_related_subscriber_first_name} ${claimantData.claimant_related_subscriber_last_name}`
      : claimantData?.claimant_related_subscriber_name;

  // Step 1: Verify claimant data
  if (step === 1) {
    return (
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Please verify the claimant information before sending the <strong>{template.title}</strong> document.
          </Typography>
        </Box>

        <Box
          sx={{
            bgcolor: "action.hover",
            borderRadius: 1.5,
            p: 2,
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <DataRow
                label={getProps("claimant_name", ResourceName.CLAIMANTS).label}
                value={claimantData?.claimant_name}
                isLoading={isLoading}
              />
              <DataRow
                label={getProps("client_id_ref", ResourceName.CLAIMANTS).label}
                value={client?.client_legal_name}
                isLoading={isLoading}
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <DataRow
                label="Subscriber"
                value={
                  subscriberDisplayName
                    ? claimantData?.claimant_relationship
                      ? `${subscriberDisplayName} (${claimantData.claimant_relationship})`
                      : subscriberDisplayName
                    : undefined
                }
                isLoading={isLoading}
              />
              <DataRow
                label={getProps("member_id", ResourceName.CLAIMANTS).label}
                value={claimantData?.member_id}
                isLoading={isLoading}
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <DataRow
                label="Policy Start"
                value={formatDate(policy?.effective_start_date as string)}
                isLoading={isLoading}
              />
              <DataRow
                label="Policy End"
                value={formatDate(policy?.effective_end_date as string)}
                isLoading={isLoading}
              />
            </Stack>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={onProceedToStep2}
            disabled={isLoading}
          >
            Continue
          </Button>
        </Stack>
      </Stack>
    );
  }

  // Step 2: Configure recipients and send
  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="body2" color="text.secondary">
          Choose who should receive and sign this document.
        </Typography>
      </Box>

      <Box>
        <Autocomplete
          options={clientContacts}
          getOptionLabel={(contact: ClientContact) =>
            contact.email ? `${contact.contact_name} (${contact.email})` : contact.contact_name
          }
          isOptionEqualToValue={(option, value) => option.email === value.email}
          value={clientContacts.find((c: ClientContact) => c.email === formData.recipient.email) || null}
          onChange={(_, selectedContact) => {
            onFormChange({
              recipient: { email: selectedContact?.email || "" },
            });
          }}
          onBlur={() => setRecipientTouched(true)}
          disabled={isSending}
          renderInput={(params) => (
            <TextField {...params} label="Send To" required size="small" placeholder="Select a contact" />
          )}
          renderOption={(props, contact: ClientContact) => (
            <li {...props} key={contact.email}>
              <Stack direction="row" alignItems="center" spacing={1} width="100%">
                <Stack flex={1}>
                  <Typography variant="body2">{contact.contact_name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {contact.email}
                    {contact.job_title ? ` · ${contact.job_title}` : ""}
                  </Typography>
                </Stack>
                {contact.is_primary && <Chip label="Primary" size="small" color="primary" />}
              </Stack>
            </li>
          )}
          noOptionsText="No verified contacts available"
        />

        {recipientTouched && !formData.recipient.email && client?.id && (
          <FormHelperText>
            Don't see who you need?{" "}
            <Link to={`/clients/${client.id}/show/overview`} style={{ color: "inherit", textDecoration: "underline" }}>
              Verify contacts on the client record
            </Link>
          </FormHelperText>
        )}
      </Box>

      <Autocomplete
        multiple
        options={sortedUsers}
        loading={isLoadingUsers}
        disabled={isSending}
        getOptionLabel={(user) => {
          const userName = user.full_name || user.email || user.id;
          const isCurrentUser = currentUser && user.id === currentUser.id;
          return isCurrentUser ? `${userName} (me)` : userName;
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, selectedUsers) => {
          const recipients = selectedUsers.map((user) => ({
            email: user.email,
          }));
          onFormChange({ additional_recipients: recipients });
        }}
        renderInput={(params) => <TextField {...params} label="CC (optional)" size="small" />}
        renderOption={(props, user) => {
          const isCurrentUser = currentUser && user.id === currentUser.id;
          return (
            <li {...props} key={user.id}>
              <Stack direction="row" alignItems="center" spacing={1} width="100%">
                <Stack flex={1}>
                  <Typography variant="body2">{user.full_name || user.email || user.id}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user.email}
                    {user.job_title ? ` · ${user.job_title}` : ""}
                  </Typography>
                </Stack>
                {isCurrentUser && <Chip label="Me" size="small" variant="outlined" />}
              </Stack>
            </li>
          );
        }}
      />

      {task.signature_standing_cc?.trim() && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Standing CC
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
            <Chip label={task.signature_standing_cc.trim()} size="small" variant="outlined" sx={{ maxWidth: "100%" }} />
          </Stack>
          <FormHelperText>CC required by partner policy</FormHelperText>
        </Box>
      )}

      <TextField
        error={showReplyToError}
        label="Reply To (optional)"
        fullWidth
        value={formData.reply_to.email}
        onChange={(e) => onFormChange({ reply_to: { email: e.target.value } })}
        placeholder="Replies from recipients will be sent to this address"
        size="small"
        disabled={isSending}
        helperText={showReplyToError ? invalidEmailErrorMessage : ""}
      />

      <TextField
        label="Message (optional)"
        fullWidth
        multiline
        rows={2}
        value={formData.message}
        onChange={(e) => onFormChange({ message: e.target.value })}
        placeholder="Add a note to include with the document..."
        size="small"
        disabled={isSending}
      />

      <Stack direction="row" spacing={1.5} justifyContent="flex-end">
        <Button size="small" onClick={onCancel} disabled={isSending}>
          Back
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<SendIcon />}
          onClick={onSend}
          disabled={!formData.recipient.email || isSending}
        >
          {isSending ? "Sending..." : "Send"}
        </Button>
      </Stack>
    </Stack>
  );
};
